import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { postalQueries } from '@as-react/common-queries';
import { AutocompleteAlpha, Grid } from '@yonderland/andes-react';
import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { I18N_NAMESPACES } from '../../../../constants/i18nConstants';
import { useDebouncedQuery } from '../../../../hooks/useDebouncedQuery';
import { isValidPostCode } from '../../../../util/BuyFormUtil';
import { FormField } from '../../FormField';
const createMunicipalityOptions = (data) => (data === null || data === void 0 ? void 0 : data.map(item => ({
    text: `${item.postCode} - ${item.subMunicipality}`,
    id: `${item.postCode}-${item.subMunicipality}`,
    postCode: item.postCode,
    city: item.subMunicipality,
}))) || [];
const getAutocompleteItems = (data) => data.map(({ id, text }) => ({ id, text }));
export const AddressFieldsBEWithValidation = ({ fields, isRequired = true, }) => {
    const { t } = useTranslation(I18N_NAMESPACES.general);
    const [focusedField, setFocusedField] = useState(null);
    const { setValue } = useFormContext();
    const { defaultRequestParams, servicesEndpoint, lang } = useAemConfig();
    const { getMunicipality, getStreet, getHouseNumber, getBoxNumber } = postalQueries({
        servicesEndpoint,
        defaultRequestParams,
    });
    const [addressName, houseNumberName, houseNumberAdditionName, postCodeName, cityName] = fields;
    const [postCode, city, address, houseNumber, houseNumberAddition] = useWatch({
        name: [postCodeName, cityName, addressName, houseNumberName, houseNumberAdditionName],
    });
    const isFocussed = (fieldName) => Boolean(focusedField === fieldName);
    const { data: postCodes, isFetching: isPostCodesLoading } = useDebouncedQuery(getMunicipality({ lang, query: postCode }, {
        enabled: isFocussed(postCodeName) && postCode !== '',
        select: createMunicipalityOptions,
    }));
    const { data: cities, isFetching: isCitiesLoading } = useDebouncedQuery(getMunicipality({ lang, query: isValidPostCode(postCode, 'BE') ? postCode : city, city }, {
        enabled: isFocussed(cityName) && city !== '',
        select: data => isValidPostCode(postCode, 'BE')
            ? createMunicipalityOptions(data).filter(item => item.city.toLowerCase().includes(city.toLowerCase()))
            : createMunicipalityOptions(data),
    }));
    const { data: streets, isFetching: isStreetsLoading } = useDebouncedQuery(getStreet({ lang, municipality: city, postcode: postCode, query: address }, {
        enabled: isFocussed(addressName) && address !== '' && Boolean(postCode || city),
        select: data => (data === null || data === void 0 ? void 0 : data.map(item => ({ text: item.street, id: item.street }))) || [],
    }));
    const { data: houseNumbers, isFetching: isHouseNumberLoading } = useDebouncedQuery(getHouseNumber({ lang, municipality: city, postcode: postCode, street: address, query: houseNumber }, {
        enabled: isFocussed(houseNumberName) && houseNumber !== '' && Boolean(address && (postCode || city)),
        select: data => (data === null || data === void 0 ? void 0 : data.map(item => ({ text: item.houseNumber, id: item.houseNumber }))) || [],
    }));
    const { data: boxNumbers, isFetching: isBoxNumberLoading } = useDebouncedQuery(getBoxNumber({ lang, municipality: city, postcode: postCode, street: address, houseNumber, query: houseNumberAddition }, {
        enabled: isFocussed(houseNumberAdditionName) && Boolean(houseNumber && address && (postCode || city)),
        select: data => (data === null || data === void 0 ? void 0 : data.map(item => ({ text: item.boxNumber, id: item.boxNumber }))) || [],
    }));
    const choosePostCodeFromList = (option) => {
        const { postCode = '', city = '' } = (postCodes === null || postCodes === void 0 ? void 0 : postCodes.find(postCode => postCode.id === option.id)) || {};
        setValue(postCodeName, postCode, { shouldTouch: false, shouldValidate: true });
        setValue(cityName, city, { shouldTouch: false, shouldValidate: true });
    };
    const chooseCityFromList = (option) => {
        const { postCode = '', city = '' } = (cities === null || cities === void 0 ? void 0 : cities.find(postCode => postCode.id === option.id)) || {};
        setValue(postCodeName, postCode, { shouldTouch: false, shouldValidate: true });
        setValue(cityName, city, { shouldTouch: false, shouldValidate: true });
    };
    const chooseStreetFromList = (option) => {
        setValue(addressName, option.id, { shouldTouch: false, shouldValidate: true });
    };
    const chooseHouseNumberFromList = (option) => {
        setValue(houseNumberName, option.id, { shouldTouch: false, shouldValidate: true });
    };
    const chooseBoxNumberFromList = (option) => {
        setValue(houseNumberAdditionName, option.id, { shouldTouch: false, shouldValidate: true });
    };
    return (_jsxs(Grid, { fullWidth: true, columns: 12, gapY: 6, gapX: 4, isQueryContainer: true, children: [_jsx(Grid.Cell, { span: { base: 12, ['@lg']: 6 }, children: _jsx(FormField, { name: postCodeName, required: isRequired, children: (_a) => {
                        var _b = _a.fieldProps, { ref } = _b, fieldProps = __rest(_b, ["ref"]), { error } = _a;
                        return (_jsx(AutocompleteAlpha, { testId: "postcode", id: fieldProps.name, items: getAutocompleteItems(postCode ? postCodes || [] : []), input: {
                                innerRef: ref,
                                id: fieldProps.name,
                                name: fieldProps.name,
                                label: t('address.postCode'),
                                placeholder: t('placeholder.postal.code.be'),
                                optionalText: isRequired ? undefined : t('form.field.optional'),
                                value: fieldProps.value || '',
                                error: error,
                                required: isRequired,
                                minLength: 4,
                                maxLength: 50,
                                onFocus: () => setFocusedField(fieldProps.name),
                                onBlur: () => {
                                    setFocusedField(null);
                                    fieldProps.onBlur();
                                },
                            }, onSelectedItemChange: choosePostCodeFromList, onChange: fieldProps.onChange, isLoading: isPostCodesLoading }));
                    } }) }), _jsx(Grid.Cell, { span: { base: 12, ['@lg']: 6 }, children: _jsx(FormField, { name: cityName, required: isRequired, children: (_a) => {
                        var _b = _a.fieldProps, { ref } = _b, fieldProps = __rest(_b, ["ref"]), { error } = _a;
                        return (_jsx(AutocompleteAlpha, { testId: "town_city", id: fieldProps.name, items: getAutocompleteItems(city ? cities || [] : []), input: {
                                innerRef: ref,
                                id: fieldProps.name,
                                name: fieldProps.name,
                                label: t('address.city'),
                                placeholder: t('address.city.choose'),
                                optionalText: isRequired ? undefined : t('form.field.optional'),
                                value: fieldProps.value || '',
                                error: error,
                                required: isRequired,
                                onFocus: () => setFocusedField(fieldProps.name),
                                onBlur: () => {
                                    setFocusedField(null);
                                    fieldProps.onBlur();
                                },
                            }, onSelectedItemChange: chooseCityFromList, onChange: fieldProps.onChange, isLoading: isCitiesLoading }));
                    } }) }), _jsx(Grid.Cell, { span: { base: 12, ['@lg']: 6 }, children: _jsx(FormField, { name: addressName, required: isRequired, children: (_a) => {
                        var _b = _a.fieldProps, { ref } = _b, fieldProps = __rest(_b, ["ref"]), { error } = _a;
                        return (_jsx(AutocompleteAlpha, { testId: "address_line1", id: fieldProps.name, items: addressName ? streets || [] : [], input: {
                                innerRef: ref,
                                id: fieldProps.name,
                                name: fieldProps.name,
                                label: t('address.street'),
                                optionalText: isRequired ? undefined : t('form.field.optional'),
                                value: fieldProps.value || '',
                                error: error,
                                required: isRequired,
                                maxLength: 500,
                                onFocus: () => setFocusedField(fieldProps.name),
                                onBlur: () => {
                                    setFocusedField(null);
                                    fieldProps.onBlur();
                                },
                            }, onSelectedItemChange: chooseStreetFromList, onChange: fieldProps.onChange, isLoading: isStreetsLoading }));
                    } }) }), _jsx(Grid.Cell, { span: { base: 6, ['@lg']: 3 }, children: _jsx(FormField, { name: houseNumberName, required: isRequired, children: (_a) => {
                        var _b = _a.fieldProps, { ref } = _b, fieldProps = __rest(_b, ["ref"]), { error } = _a;
                        return (_jsx(AutocompleteAlpha, { testId: "houseNumber", id: fieldProps.name, items: houseNumber ? houseNumbers || [] : [], input: {
                                innerRef: ref,
                                id: fieldProps.name,
                                name: fieldProps.name,
                                label: t('address.houseNumber'),
                                optionalText: isRequired ? undefined : t('form.field.optional'),
                                value: fieldProps.value || '',
                                error: error,
                                required: isRequired,
                                maxLength: 20,
                                onFocus: () => setFocusedField(fieldProps.name),
                                onBlur: () => {
                                    setFocusedField(null);
                                    fieldProps.onBlur();
                                },
                            }, onSelectedItemChange: chooseHouseNumberFromList, onChange: fieldProps.onChange, isLoading: isHouseNumberLoading }));
                    } }) }), _jsx(Grid.Cell, { span: { base: 6, ['@lg']: 3 }, children: _jsx(FormField, { name: houseNumberAdditionName, children: (_a) => {
                        var _b = _a.fieldProps, { ref } = _b, fieldProps = __rest(_b, ["ref"]), { error } = _a;
                        return (_jsx(AutocompleteAlpha, { testId: "houseNumberAddition", id: fieldProps.name, items: boxNumbers || [], input: {
                                innerRef: ref,
                                id: fieldProps.name,
                                name: fieldProps.name,
                                label: t('address.houseNumberAddition'),
                                optionalText: t('form.field.optional'),
                                value: fieldProps.value || '',
                                error: error,
                                maxLength: 20,
                                onFocus: () => setFocusedField(fieldProps.name),
                                onBlur: () => {
                                    setFocusedField(null);
                                    fieldProps.onBlur();
                                },
                            }, onSelectedItemChange: chooseBoxNumberFromList, onChange: fieldProps.onChange, isLoading: isBoxNumberLoading }));
                    } }) })] }));
};
